import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import "./PayrollHRConfirmation.scss";
import { toast } from "react-toastify";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import IconButton from "../../../components/IconButton/IconButton";

function PayrollHRConfirmation({
  payrollId,
  selectedMonth,
  selectedYear,
  refreshTrigger,
}) {
  const [payrollData, setPayrollData] = useState([]);
  const [originalPayrollData, setOriginalPayrollData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [onConfirmSubmit, setOnConfirmSubmit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // New state for the edit mode cancellation warning
  const [showCancelEditWarning, setShowCancelEditWarning] = useState(false);

  const fetchPayrollData = async () => {
    if (!payrollId || payrollId === 0) {
      setLoading(false);
      setPayrollData([]);
      setOriginalPayrollData([]);
      return;
    }

    let calculatedPayrollDate = "";
    if (selectedMonth) {
      const currentYear = selectedYear || new Date().getFullYear();
      calculatedPayrollDate = `${currentYear}-${selectedMonth.number
        .toString()
        .padStart(2, "0")}-01`;
    }

    if (!calculatedPayrollDate) {
      setLoading(false);
      setPayrollData([]);
      setOriginalPayrollData([]);
      return;
    }

    try {
      const requestBody = {
        payrollId: payrollId,
        payrollDate: calculatedPayrollDate,
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/report/payroll-sheet`,
        requestBody
      );

      const responseData = response.data.data || [];

      console.log(responseData);

      setPayrollData(responseData);
      setOriginalPayrollData(JSON.parse(JSON.stringify(responseData)));
      setError("");
      setLoading(false);
      setHasChanges(false);
      setNeedsRecalculation(false);
    } catch (err) {
      setError("Failed to fetch payroll data");
      console.error("Error fetching payroll data:", err);
      setLoading(false);
    }
  };

  const transformRecalculateResponse = (
    recalculateData,
    currentPayrollData
  ) => {
    return currentPayrollData.map((currentEmployee) => {
      const recalculatedEmployee = recalculateData.find(
        (emp) => emp.employeeId === currentEmployee.employeeId
      );

      if (!recalculatedEmployee) {
        return currentEmployee;
      }

      const transformComponents = (
        components,
        salaryType,
        contributionType
      ) => {
        return components
          .filter(
            (comp) =>
              comp.salaryType === salaryType &&
              comp.contributionType === contributionType
          )
          .map((comp) => ({
            salaryName: comp.salaryName,
            value: comp.amount,
            paymentSetupId: comp.paymentSetupId,
          }));
      };

      const updatedEmployee = {
        ...currentEmployee,
        earnedGrossSalary: recalculatedEmployee.earnedGrossSalary,
        earnedNetSalary: recalculatedEmployee.earnedNetSalary,
        payableAmount: recalculatedEmployee.payableAmount,
        paidAmount: recalculatedEmployee.paidAmount,
        netPay: recalculatedEmployee.earnedNetSalary,
        totalDeductions: recalculatedEmployee.employeeDeductions,
        lossOfPay: currentEmployee.lossOfPay,
        unPaidLeave: currentEmployee.lossOfPay,

        fixedEarnings: transformComponents(
          recalculatedEmployee.components,
          "EARNING",
          "EMPLOYEE"
        ),
        earnedEarnings: transformComponents(
          recalculatedEmployee.components,
          "EARNING",
          "EMPLOYEE"
        ),
        employeeContribution: transformComponents(
          recalculatedEmployee.components,
          "DEDUCTION",
          "EMPLOYEE"
        ),
        employerContribution: transformComponents(
          recalculatedEmployee.components,
          "DEDUCTION",
          "EMPLOYER"
        ),

        allowances:
          recalculatedEmployee.allowanceDTOs?.map((allowance) => ({
            allowanceId: allowance.allowanceId,
            salaryName: allowance.allowanceName,
            value: allowance.amount,
          })) ||
          currentEmployee.allowances ||
          [],

        otherDeductions:
          recalculatedEmployee.deductionDTOs?.map((deduction) => ({
            deductionId: deduction.deductionId,
            salaryName: deduction.deductionName,
            value: deduction.amount,
          })) ||
          currentEmployee.otherDeductions ||
          [],

        reimbursement:
          recalculatedEmployee.reimbursements?.map((reimb) => ({
            reimbursementHistoryId: reimb.reimbursementHistoryId,
            salaryName: reimb.reimbursementName,
            value: reimb.amount,
            type: reimb.type,
          })) ||
          currentEmployee.reimbursement ||
          [],

        loans:
          recalculatedEmployee.emergencyFund
            ?.filter((fund) => fund.fundType === "LOAN")
            .map((loan) => ({
              fundId: loan.fundId,
              salaryName: "Loan Installment",
              value: loan.amount,
              paymentDate: loan.paymentDate,
            })) ||
          currentEmployee.loans ||
          [],
      };

      return updatedEmployee;
    });
  };

  useEffect(() => {
    fetchPayrollData();
  }, [payrollId, selectedMonth, refreshTrigger]);

  const getUniqueComponents = (componentType) => {
    const components = new Set();
    payrollData.forEach((item) => {
      if (item[componentType] && Array.isArray(item[componentType])) {
        item[componentType].forEach((component) => {
          components.add(component.salaryName);
        });
      }
    });
    return Array.from(components);
  };

  const getComponentValue = (employeeData, componentType, componentName) => {
    if (
      !employeeData[componentType] ||
      !Array.isArray(employeeData[componentType])
    )
      return 0;

    const component = employeeData[componentType].find(
      (c) => c.salaryName === componentName
    );

    return component ? component.value : 0;
  };

  // Helper function to scroll to a specific column
  const scrollToColumn = useCallback((columnClass, delay = 0) => {
    setTimeout(() => {
      const tableContainer = document.querySelector(".table-container > div");
      const targetColumn = document.querySelector(`.${columnClass}`);

      if (tableContainer && targetColumn) {
        const containerRect = tableContainer.getBoundingClientRect();
        const targetRect = targetColumn.getBoundingClientRect();
        const scrollLeft =
          targetRect.left -
          containerRect.left +
          tableContainer.scrollLeft -
          300;

        tableContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });
      }
    }, delay);
  }, []);

  const handleEditChange = useCallback(
    (employeeIndex, componentType, componentName, value) => {
      // Validate numeric input
      if (value !== "" && (isNaN(value) || value < 0)) {
        toast.warn("Please enter a valid positive number.");
        return;
      }

      setPayrollData((prevData) => {
        const updatedData = [...prevData];
        const employee = updatedData[employeeIndex];

        if (!employee[componentType]) {
          employee[componentType] = [];
        }

        const componentIndex = employee[componentType].findIndex(
          (c) => c.salaryName === componentName
        );

        if (componentIndex !== -1) {
          employee[componentType][componentIndex].value =
            value === "" ? 0 : parseFloat(value);
        } else {
          // Create new component if it doesn't exist
          const newComponent = {
            salaryName: componentName,
            value: value === "" ? 0 : parseFloat(value),
          };

          // Add IDs if the component type requires them
          if (componentType === "allowances") {
            newComponent.allowanceId = 0;
          } else if (componentType === "otherDeductions") {
            newComponent.deductionId = 0;
          }

          employee[componentType].push(newComponent);
        }

        return updatedData;
      });

      setHasChanges(true);
      setNeedsRecalculation(true);

      // No auto-scroll during editing - user can scroll manually
    },
    []
  );

  // Handle cancelling edit mode with unsaved changes
  const handleCancelEdit = useCallback(() => {
    setPayrollData(JSON.parse(JSON.stringify(originalPayrollData)));
    setHasChanges(false);
    setIsEditMode(false);
    setNeedsRecalculation(false);
    setShowCancelEditWarning(false);
  }, [originalPayrollData]);

  // Toggle edit mode with WarningPopup instead of window.confirm
  const toggleEditMode = useCallback(() => {
    if (isEditMode && hasChanges) {
      // Show warning popup instead of window.confirm
      setShowCancelEditWarning(true);
    } else {
      setIsEditMode(!isEditMode);
      if (!isEditMode) {
        // Entering edit mode - scroll to first editable section (allowances)
        setOriginalPayrollData(JSON.parse(JSON.stringify(payrollData)));
        const allowanceNames = getUniqueComponents("allowances");
        if (allowanceNames.length > 0) {
          scrollToColumn("allowance-column", 300);
        } else {
          // If no allowances, try reimbursements
          const reimbursementNames = getUniqueComponents("reimbursement");
          if (reimbursementNames.length > 0) {
            scrollToColumn("reimbursement-column", 300);
          } else {
            // If no reimbursements, try other deductions
            const otherDeductionNames = getUniqueComponents("otherDeductions");
            if (otherDeductionNames.length > 0) {
              scrollToColumn("deduction-column", 300);
            }
          }
        }
      }
    }
  }, [
    isEditMode,
    hasChanges,
    payrollData,
    scrollToColumn,
    getUniqueComponents,
  ]);

  // Helper function to get Income Tax value from employee components
  const getIncomeTaxValue = (employee) => {
    const itComponent = (employee.fixedEarnings || [])
      .concat(employee.earnedEarnings || [])
      .concat(employee.employeeContribution || [])
      .find(
        (comp) => comp.salaryName === "IT" || comp.salaryName === "Income Tax"
      );
    return itComponent ? itComponent.value : 0;
  };

  const handleRecalculate = useCallback(async () => {
    if (!payrollData || payrollData.length === 0) {
      toast.error("No payroll data to recalculate.");
      return;
    }

    if (!selectedMonth) {
      toast.error("Please select a month first.");
      return;
    }

    setIsRecalculating(true);
    setError("");

    try {
      const currentYear = new Date().getFullYear();
      const payrollDate = `${currentYear}-${selectedMonth.number
        .toString()
        .padStart(2, "0")}-01`;

      // Create payload array according to API specification
      const payload = payrollData.map((employee) => ({
        employeeId: employee.employeeId || 0,
        payrollDate: payrollDate,
        lossOfPayDays: employee.lossOfPay || 0,
        incomeTaxAmount: getIncomeTaxValue(employee), // Add income tax amount
        allowanceDTOs: (employee.allowances || []).map((allowance) => ({
          allowanceId: allowance.allowanceId || 0,
          allowanceName: allowance.salaryName || "",
          amount: allowance.value || 0,
        })),
        deductionDTOs: (employee.otherDeductions || []).map((deduction) => ({
          deductionId: deduction.deductionId || 0,
          deductionName: deduction.salaryName || "",
          amount: deduction.value || 0,
        })),
      }));

      console.log("Recalculate payload:", JSON.stringify(payload, null, 2));

      // Updated endpoint URL
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/recalculate-payroll-history-list`,
        payload
      );

      console.log("Recalculate response:", response.data);

      const responseData = response.data.data || [];

      if (responseData.length > 0) {
        const updatedPayrollData = transformRecalculateResponse(
          responseData,
          payrollData
        );

        const finalUpdatedData = updatedPayrollData.map(
          (newEmployee, index) => {
            const currentEmployee = payrollData[index];
            return {
              ...newEmployee,
              lossOfPay: currentEmployee.lossOfPay,
              unPaidLeave: currentEmployee.lossOfPay,
            };
          }
        );

        setPayrollData(finalUpdatedData);
        setHasChanges(true);
        setNeedsRecalculation(false);

        toast.success(
          `Recalculated payroll for ${responseData.length} employees. Values updated in the table.`,
          { icon: "🔄" }
        );
      } else {
        toast.info("No data returned from recalculation.", { icon: "ℹ️" });
      }
    } catch (error) {
      console.error("Error recalculating payroll:", error);
      console.error("Error response:", error.response?.data);
      const errorMessage =
        error.response?.data?.message || "Error recalculating payroll.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRecalculating(false);
    }
  }, [payrollData, selectedMonth]);

  const handleSubmit = useCallback(
    async (skipConfirmation = false) => {
      if (!skipConfirmation && !hasChanges && payrollData.length > 0) {
        setOnConfirmSubmit(() => () => handleSubmit(true));
        setShowWarningPopup(true);
        return;
      }

      if (!selectedMonth) {
        toast.error("Please select a month first.");
        return;
      }

      const currentYear = new Date().getFullYear();
      const payrollDate = `${currentYear}-${selectedMonth.number
        .toString()
        .padStart(2, "0")}-01`;

      // Updated payload structure to match new API specification
      const payload = payrollData.map((employee) => ({
        empPayrollHistoryId: employee.employeePayrollHistoryId || 0,
        updatedAllowances: (employee.allowances || []).map((allowance) => ({
          allowanceId: allowance.allowanceId || 0,
          allowanceName: allowance.salaryName || "",
          amount: allowance.value || 0,
        })),
        updatedDeductions: (employee.otherDeductions || []).map(
          (deduction) => ({
            deductionId: deduction.deductionId || 0,
            deductionName: deduction.salaryName || "",
            amount: deduction.value || 0,
          })
        ),
        // Include all the required fields from the table
        earnedGrossSalary: employee.earnedGrossSalary || 0,
        earnedOtherAllowances: employee.earnedOtherAllowances || 0,
        earnedOtherDeductions: employee.earnedOtherDeductions || 0,
        earnedNetSalary: employee.earnedNetSalary || 0,
        earnedCTC: employee.earnedCTC || employee.ctcAmount || 0,
        actualGrossSalary:
          employee.actualGrossSalary || employee.earnedGrossSalary || 0,
        actualNetSalary:
          employee.actualNetSalary || employee.earnedNetSalary || 0,
        actualCTC: employee.actualCTC || employee.ctcAmount || 0,
        payableAmount: employee.payableAmount || 0,
        paidAmount: employee.paidAmount || 0,
        employeeDeductions: employee.totalDeductions || 0,
        employerDeductions: employee.employerDeductions || 0,
        payrollDate: payrollDate,
        paymentMode: employee.paymentMode || "NEFT",
        // Include allowanceDTOs and deductionDTOs for compatibility
        allowanceDTOs: (employee.allowances || []).map((allowance) => ({
          allowanceId: allowance.allowanceId || 0,
          allowanceName: allowance.salaryName || "",
          amount: allowance.value || 0,
        })),
        deductionDTOs: (employee.otherDeductions || []).map((deduction) => ({
          deductionId: deduction.deductionId || 0,
          deductionName: deduction.salaryName || "",
          amount: deduction.value || 0,
        })),
      }));

      if (payload.length === 0) {
        toast.error("No data to submit.");
        return;
      }

      console.log("Submit payload:", JSON.stringify(payload, null, 2));

      setIsSubmitting(true);

      try {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_ACCOUNTANT_URL}/payroll-history/list`,
          payload
        );

        toast.success("Payroll updated successfully! 🎉", {
          autoClose: 3000,
        });

        setHasChanges(false);
        setIsEditMode(false); // Switch off edit mode after successful submit
        await fetchPayrollData();
      } catch (error) {
        console.error("Error updating payroll:", error);
        const errorMessage =
          error.response?.data?.message ||
          "There was an error updating the payroll.";
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [payrollData, hasChanges, selectedMonth, fetchPayrollData]
  );

  const handleDelete = useCallback(async (employeePayrollHistoryId) => {
    if (!employeePayrollHistoryId) {
      toast.error("Invalid payroll record ID.");
      return;
    }

    setPendingDeleteId(employeePayrollHistoryId);
    setShowWarning(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;

    setIsDeleting(true);
    setShowWarning(false);

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/payroll-history/${pendingDeleteId}/delete-payroll-history`
      );

      toast.success("Payroll record deleted successfully! 🗑️", {
        position: "top-center",
        autoClose: 3000,
      });

      await fetchPayrollData();
    } catch (error) {
      console.error("Error deleting payroll record:", error);
      const errorMessage =
        error.response?.data?.message ||
        "There was an error deleting the payroll record.";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
    }
  }, [pendingDeleteId, fetchPayrollData]);

  if (loading) return <LoadingScreen />;

  if (error) return <div style={{ color: "red" }}>{error}</div>;

  // Determine which fields are editable (removed lossOfPay from editable fields)
  const isFieldEditable = (componentType) => {
    return (
      isEditMode &&
      (componentType === "allowances" ||
        componentType === "reimbursement" ||
        componentType === "otherDeductions")
    );
  };

  const fixedEarningsNames = getUniqueComponents("fixedEarnings");
  const earnedEarningsNames = getUniqueComponents("earnedEarnings");
  const allowanceNames = getUniqueComponents("allowances");
  const reimbursementNames = getUniqueComponents("reimbursement");
  const loanNames = getUniqueComponents("loans");
  const employeeContributionNames = getUniqueComponents("employeeContribution");
  const otherDeductionNames = getUniqueComponents("otherDeductions");
  const employerContributionNames = getUniqueComponents("employerContribution");

  const calculateTotals = () => {
    return payrollData.reduce(
      (acc, employee) => {
        acc.totalGross += employee.earnedGrossSalary || 0;
        acc.totalDeductions += employee.totalDeductions || 0;
        acc.totalNet += employee.netPay || 0;
        acc.totalEmployer += (employee.employerContribution || []).reduce(
          (sum, contrib) => sum + (contrib.value || 0),
          0
        );
        return acc;
      },
      {
        totalGross: 0,
        totalDeductions: 0,
        totalNet: 0,
        totalEmployer: 0,
      }
    );
  };

  const totals = payrollData.length > 0 ? calculateTotals() : null;

  return (
    <div className="payroll-hr-confirmation">
      {totals && (
        <div className="salary-registry__summary">
          <div className="stats-grid">
            <div className="stat-card total-gross">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>₹{totals.totalGross.toLocaleString()}</h3>
                <p>Total Gross Salary</p>
              </div>
            </div>
            <div className="stat-card total-deductions">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">📉</div>
              <div className="stat-content">
                <h3>₹{totals.totalDeductions.toLocaleString()}</h3>
                <p>Total Deductions</p>
              </div>
            </div>
            <div className="stat-card total-net">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💵</div>
              <div className="stat-content">
                <h3>₹{totals.totalNet.toLocaleString()}</h3>
                <p>Total Net Pay</p>
              </div>
            </div>
            <div className="stat-card total-employer">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>₹{totals.totalEmployer.toLocaleString()}</h3>
                <p>Total Employer Contribution</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {payrollData.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "10px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <ButtonWithIcon
            text={isEditMode ? "Cancel Edit" : "Edit Payroll"}
            icon={isEditMode ? "mdi:close" : "mdi:pencil"}
            onClick={toggleEditMode}
            disabled={isRecalculating || isSubmitting}
            className={isEditMode ? "secondary" : "primary"}
          />

          {isEditMode && (
            <>
              <ButtonWithIcon
                text={isRecalculating ? "Recalculating..." : "Recalculate"}
                icon={
                  isRecalculating ? "mdi:loading mdi-spin" : "mdi:calculator"
                }
                onClick={handleRecalculate}
                disabled={isRecalculating || isSubmitting}
                className="warning"
              />

              <ButtonWithIcon
                text={isSubmitting ? "Updating..." : "Update"}
                icon={
                  isSubmitting ? "mdi:loading mdi-spin" : "mdi:check-circle"
                }
                onClick={handleSubmit}
                disabled={isRecalculating || isSubmitting || needsRecalculation}
                className="success"
              />
            </>
          )}

          {isEditMode && needsRecalculation && (
            <span
              style={{
                color: "#dc2626",
                fontWeight: 600,
                fontSize: "0.9rem",
                padding: "8px 12px",
                backgroundColor: "#fecaca",
                borderRadius: "6px",
              }}
            >
              🔄 Please recalculate before updating
            </span>
          )}

          {isEditMode && (
            <span
              style={{
                color: "#059669",
                fontWeight: 600,
                fontSize: "0.85rem",
                padding: "6px 10px",
                backgroundColor: "#d1fae5",
                borderRadius: "6px",
                border: "1px solid #a7f3d0",
              }}
            >
              💡 Tip: Fill Allowances → Auto-scroll to Reimbursements →
              Deductions
            </span>
          )}
        </div>
      )}

      <div className="table-container">
        <div
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "80vh" }}
        >
          <table className="payroll-table">
            <thead>
              {/* Main Header Row (Groups) */}
              <tr>
                <th rowSpan="2" className="fixed-column employee-column">
                  Employee Name
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Employee ID
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Period
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Branch
                </th>
                <th rowSpan="2" className="scrollable-column">
                  CTC Amount
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Fixed Gross Salary
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Fixed Net Salary
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Base Days
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Loss of Pay
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Paid Days
                </th>
                <th rowSpan="2" className="scrollable-column">
                  Earned Gross Salary
                </th>

                {/* Fixed Salary Group */}
                {fixedEarningsNames.length > 0 && (
                  <th
                    colSpan={fixedEarningsNames.length}
                    className="fixed-earnings-column"
                  >
                    Fixed Salary
                  </th>
                )}

                {/* Earned Salary Group */}
                {earnedEarningsNames.length > 0 && (
                  <th
                    colSpan={earnedEarningsNames.length}
                    className="earned-earnings-column"
                  >
                    Earned Salary
                  </th>
                )}

                {/* Benefits Group */}
                {(allowanceNames.length > 0 ||
                  reimbursementNames.length > 0) && (
                  <th
                    colSpan={
                      allowanceNames.length + reimbursementNames.length + 1
                    }
                    className="allowance-column"
                  >
                    💚 Benefits {isEditMode && "(Editable)"}
                  </th>
                )}

                {/* Deductions Group */}
                {(loanNames.length > 0 ||
                  employeeContributionNames.length > 0 ||
                  otherDeductionNames.length > 0) && (
                  <th
                    colSpan={
                      loanNames.length +
                      employeeContributionNames.length +
                      otherDeductionNames.length +
                      1
                    }
                    className="deduction-column"
                  >
                    ❤️ Deductions {isEditMode && "(Some Editable)"}
                  </th>
                )}

                <th rowSpan="2" className="scrollable-column">
                  Total Deductions
                </th>
                <th rowSpan="2" className="scrollable-column net-pay-column">
                  Net Pay
                </th>

                {/* Employer Contributions Group */}
                {employerContributionNames.length > 0 && (
                  <th
                    colSpan={employerContributionNames.length}
                    className="employer-contrib-column"
                  >
                    Employer Contributions
                  </th>
                )}

                <th rowSpan="2" className="fixed-action-column">
                  Actions
                </th>
              </tr>

              {/* Sub-Header Row (Individual Components) */}
              <tr>
                {/* Fixed Earnings Sub-Headers */}
                {fixedEarningsNames.map((name) => (
                  <th
                    key={`fixed-${name}`}
                    className="fixed-earnings-column scrollable-column"
                  >
                    {name}
                  </th>
                ))}

                {/* Earned Earnings Sub-Headers */}
                {earnedEarningsNames.map((name) => (
                  <th
                    key={`earned-${name}`}
                    className="earned-earnings-column scrollable-column"
                  >
                    {name}
                  </th>
                ))}

                {/* Allowances Sub-Headers - Now Conditionally Editable */}
                {allowanceNames.map((name) => (
                  <th
                    key={`allowance-${name}`}
                    className={`allowance-column scrollable-column ${
                      isFieldEditable("allowances") ? "editable-header" : ""
                    }`}
                  >
                    {name} {isFieldEditable("allowances") && "✏️"}
                  </th>
                ))}

                {/* Earned Other Allowances Column */}
                <th className="allowance-column scrollable-column">
                  Total Earned Other Allowances
                </th>

                {/* Reimbursements Sub-Headers - Now Conditionally Editable */}
                {reimbursementNames.map((name) => (
                  <th
                    key={`reimbursement-${name}`}
                    className={`reimbursement-column scrollable-column ${
                      isFieldEditable("reimbursement") ? "editable-header" : ""
                    }`}
                  >
                    {name} {isFieldEditable("reimbursement") && "✏️"}
                  </th>
                ))}

                {/* Loans Sub-Headers */}
                {loanNames.map((name) => (
                  <th
                    key={`loan-${name}`}
                    className="deduction-column  scrollable-column"
                  >
                    {name}
                  </th>
                ))}

                {/* Employee Contributions Sub-Headers */}
                {employeeContributionNames.map((name) => (
                  <th
                    key={`emp-contrib-${name}`}
                    className="deduction-column scrollable-column"
                  >
                    {name}
                  </th>
                ))}

                {/* Other Deductions Sub-Headers - Now Conditionally Editable */}
                {otherDeductionNames.map((name) => (
                  <th
                    key={`other-deduction-${name}`}
                    className={`deduction-column scrollable-column ${
                      isFieldEditable("otherDeductions")
                        ? "editable-header"
                        : ""
                    }`}
                  >
                    {name} {isFieldEditable("otherDeductions") && "✏️"}
                  </th>
                ))}

                {/* Total Other Deductions Column */}
                <th className="deduction-column scrollable-column">
                  Total Other Deductions
                </th>

                {/* Employer Contributions Sub-Headers */}
                {employerContributionNames.map((name) => (
                  <th
                    key={`employer-contrib-${name}`}
                    className="employer-contrib-column scrollable-column"
                  >
                    {name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {payrollData.length > 0 ? (
                payrollData.map((item, index) => (
                  <tr key={index}>
                    <td className="fixed-column employee-column">
                      {item.accountHolderName}
                    </td>
                    <td className="scrollable-column">
                      {item.userEmployeeId || "N/A"}
                    </td>
                    <td className="scrollable-column">{item.period}</td>
                    <td className="scrollable-column">{item.branch}</td>
                    <td className="scrollable-column amount-cell">
                      ₹{item.ctcAmount?.toLocaleString() || 0}
                    </td>
                    <td className="scrollable-column amount-cell">
                      ₹{item.fixedGrossSalary?.toLocaleString() || 0}
                    </td>
                    <td className="scrollable-column amount-cell">
                      ₹{item.fixedNetSalary?.toLocaleString() || 0}
                    </td>
                    <td className="scrollable-column">{item.baseDays || 0}</td>

                    {/* Loss of Pay - Now Read-Only */}
                    <td className="scrollable-column loss-of-pay">
                      {item.lossOfPay || 0}
                    </td>

                    <td className="scrollable-column">{item.paidDays || 0}</td>
                    <td className="scrollable-column amount-cell">
                      ₹{item.earnedGrossSalary?.toLocaleString() || 0}
                    </td>

                    {/* Fixed Earnings */}
                    {fixedEarningsNames.map((name) => (
                      <td
                        key={`fixed-${name}-${index}`}
                        className="fixed-earnings-column scrollable-column"
                      >
                        ₹
                        {getComponentValue(
                          item,
                          "fixedEarnings",
                          name
                        )?.toLocaleString() || "-"}
                      </td>
                    ))}

                    {/* Earned Earnings */}
                    {earnedEarningsNames.map((name) => (
                      <td
                        key={`earned-${name}-${index}`}
                        className="earned-earnings-column scrollable-column"
                      >
                        ₹
                        {getComponentValue(
                          item,
                          "earnedEarnings",
                          name
                        )?.toLocaleString() || "-"}
                      </td>
                    ))}

                    {/* Allowances - Conditionally Editable */}
                    {allowanceNames.map((name) => {
                      const value = getComponentValue(item, "allowances", name);
                      return (
                        <td
                          key={`allowance-${name}-${index}`}
                          className={`allowance-column scrollable-column ${
                            isFieldEditable("allowances") ? "editable-cell" : ""
                          }`}
                        >
                          {isFieldEditable("allowances") ? (
                            <input
                              type="number"
                              value={
                                value === 0 || value == null || value === ""
                                  ? ""
                                  : value
                              }
                              placeholder="0.00"
                              onChange={(e) =>
                                handleEditChange(
                                  index,
                                  "allowances",
                                  name,
                                  e.target.value
                                )
                              }
                              min="0"
                              step="0.01"
                              className="editable-input"
                              title={`Edit ${name} allowance amount`}
                            />
                          ) : (
                            `₹${value?.toLocaleString() || "-"}`
                          )}
                        </td>
                      );
                    })}

                    {/* Earned Other Allowances Column */}
                    <td className="allowance-column scrollable-column amount-cell">
                      ₹{item.earnedOtherAllowances?.toLocaleString() || 0}
                    </td>

                    {/* Reimbursements - Conditionally Editable */}
                    {reimbursementNames.map((name) => {
                      const value = getComponentValue(
                        item,
                        "reimbursement",
                        name
                      );
                      return (
                        <td
                          key={`reimbursement-${name}-${index}`}
                          className={`reimbursement-column scrollable-column ${
                            isFieldEditable("reimbursement")
                              ? "editable-cell"
                              : ""
                          }`}
                        >
                          {isFieldEditable("reimbursement") ? (
                            <input
                              type="number"
                              value={
                                value === 0 || value == null || value === ""
                                  ? ""
                                  : value
                              }
                              placeholder="0.00"
                              onChange={(e) =>
                                handleEditChange(
                                  index,
                                  "reimbursement",
                                  name,
                                  e.target.value
                                )
                              }
                              min="0"
                              step="0.01"
                              className="editable-input"
                              title={`Edit ${name} reimbursement amount`}
                            />
                          ) : (
                            `₹${value?.toLocaleString() || "-"}`
                          )}
                        </td>
                      );
                    })}

                    {/* Loans */}
                    {loanNames.map((name) => (
                      <td
                        key={`loan-${name}-${index}`}
                        className="loan-column scrollable-column"
                      >
                        ₹
                        {getComponentValue(
                          item,
                          "loans",
                          name
                        )?.toLocaleString() || "-"}
                      </td>
                    ))}

                    {/* Employee Contributions */}
                    {employeeContributionNames.map((name) => (
                      <td
                        key={`emp-contrib-${name}-${index}`}
                        className="deduction-column scrollable-column"
                      >
                        ₹
                        {getComponentValue(
                          item,
                          "employeeContribution",
                          name
                        )?.toLocaleString() || "-"}
                      </td>
                    ))}

                    {/* Other Deductions - Conditionally Editable */}
                    {otherDeductionNames.map((name) => {
                      const value = getComponentValue(
                        item,
                        "otherDeductions",
                        name
                      );
                      return (
                        <td
                          key={`other-deduction-${name}-${index}`}
                          className={`deduction-column scrollable-column ${
                            isFieldEditable("otherDeductions")
                              ? "editable-cell"
                              : ""
                          }`}
                        >
                          {isFieldEditable("otherDeductions") ? (
                            <input
                              type="number"
                              value={
                                value === 0 || value == null || value === ""
                                  ? ""
                                  : value
                              }
                              placeholder="0.00"
                              onChange={(e) =>
                                handleEditChange(
                                  index,
                                  "otherDeductions",
                                  name,
                                  e.target.value
                                )
                              }
                              min="0"
                              step="0.01"
                              className="editable-input"
                              title={`Edit ${name} deduction amount`}
                            />
                          ) : (
                            `₹${value?.toLocaleString() || "-"}`
                          )}
                        </td>
                      );
                    })}

                    {/* Total Other Deductions Column */}
                    <td className="deduction-column scrollable-column amount-cell">
                      ₹{item.earnedReimbursements?.toLocaleString() || 0}
                    </td>

                    <td className="scrollable-column amount-cell deduction-total">
                      ₹{item.totalDeductions?.toLocaleString() || 0}
                    </td>

                    <td className="scrollable-column net-pay-column amount-cell">
                      ₹{item.netPay?.toLocaleString() || 0}
                    </td>

                    {/* Employer Contributions */}
                    {employerContributionNames.map((name) => (
                      <td
                        key={`employer-contrib-${name}-${index}`}
                        className="employer-contrib-column scrollable-column"
                      >
                        ₹
                        {getComponentValue(
                          item,
                          "employerContribution",
                          name
                        )?.toLocaleString() || "-"}
                      </td>
                    ))}

                    <td className="fixed-action-column">
                      <IconButton
                        icon="fluent:delete-28-regular"
                        onClick={() =>
                          handleDelete(item.employeePayrollHistoryId)
                        }
                        variant="delete"
                        disabled={isDeleting || isSubmitting || isRecalculating}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      15 + // Base columns
                      fixedEarningsNames.length +
                      earnedEarningsNames.length +
                      allowanceNames.length +
                      reimbursementNames.length +
                      loanNames.length +
                      employeeContributionNames.length +
                      otherDeductionNames.length +
                      employerContributionNames.length
                    }
                    className="no-data"
                  >
                    No payroll data found for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Warning Popup for Submit Confirmation */}
      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => {
          setShowWarningPopup(false);
          onConfirmSubmit?.();
        }}
        onDecline={() => setShowWarningPopup(false)}
        title="No Changes Detected"
        message="No changes were made. Do you still want to submit the payroll?"
        acceptText="Yes, Submit"
        declineText="Cancel"
      />

      {/* Warning Popup for Delete Confirmation */}
      <WarningPopup
        isOpen={showWarning}
        onClose={() => {
          setShowWarning(false);
          setPendingDeleteId(null);
        }}
        onAccept={confirmDelete}
        onDecline={() => {
          setShowWarning(false);
          setPendingDeleteId(null);
        }}
        title="Confirm Deletion"
        message="Are you sure you want to delete this payroll record? This action cannot be undone."
        acceptText="Yes, Delete"
        declineText="Cancel"
      />

      {/* Warning Popup for Cancel Edit Mode */}
      <WarningPopup
        isOpen={showCancelEditWarning}
        onClose={() => setShowCancelEditWarning(false)}
        onAccept={handleCancelEdit}
        onDecline={() => setShowCancelEditWarning(false)}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard them and exit edit mode?"
        acceptText="Yes, Discard Changes"
        declineText="Continue Editing"
      />
    </div>
  );
}

export default PayrollHRConfirmation;
